import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Tag, Share2 } from "lucide-react";
import { ARTICLES } from "../data/articles";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { buildWaUrl } from "../data/site";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) return <Navigate to="/blog" replace />;

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: [article.cover],
    datePublished: article.date,
    author: { "@type": "Organization", name: "ListrikBatam.com" },
    publisher: {
      "@type": "Organization",
      name: "ListrikBatam.com",
    },
  };

  return (
    <div data-testid="article-detail-page">
      <Seo
        title={`${article.title} | Blog ListrikBatam.com`}
        description={article.excerpt}
        canonical={`https://listrikbatam.com/blog/${article.slug}`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-24 pb-12">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F4C75] hover:text-[#FF6B00]"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" /> Kembali ke Blog
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF6B00]">
              <Tag className="w-3 h-3" /> {article.category}
            </div>
            <h1 className="mt-3 font-display text-3xl md:text-5xl lg:text-6xl font-black text-[#0F4C75] leading-[1.02] tracking-tight max-w-4xl text-balance">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-[#1B262C]/65">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(article.date).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {article.readingTime}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-8">
          <div className="rounded-3xl overflow-hidden aspect-[16/9] bg-[#0F4C75] mb-10">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="prose max-w-none space-y-6 text-[#1B262C]/85 text-base md:text-lg leading-relaxed">
            <p className="text-xl md:text-2xl font-medium text-[#1B262C] leading-snug text-balance">
              {article.excerpt}
            </p>
            {article.content.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={buildWaUrl(`Saya baca artikel "${article.title}" dan ingin konsultasi.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-wa-cta"
              className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full px-6 py-3 text-sm shadow-[0_10px_24px_rgba(255,107,0,0.35)] hover:-translate-y-0.5 transition-all"
            >
              Konsultasi terkait artikel
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: article.title, url: window.location.href }).catch(() => {});
                } else {
                  navigator.clipboard?.writeText(window.location.href);
                }
              }}
              data-testid="article-share"
              className="inline-flex items-center gap-2 border border-[#E5E7EB] text-[#0F4C75] font-semibold rounded-full px-6 py-3 text-sm hover:border-[#3282B8] transition"
            >
              <Share2 className="w-4 h-4" /> Bagikan
            </button>
          </div>
        </Reveal>

        {/* Sidebar */}
        <Reveal as="aside" className="lg:col-span-4 space-y-6">
          <div className="rounded-3xl bg-[#0F4C75] text-white p-7 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-20" />
            <div className="relative">
              <h3 className="font-display text-xl font-bold">Butuh konsultasi?</h3>
              <p className="mt-2 text-sm text-white/75">
                Tim teknisi kami siap menjawab pertanyaan Anda lewat WhatsApp.
              </p>
              <a
                href={buildWaUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sidebar-wa-cta"
                className="mt-5 inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full px-5 py-2.5 text-sm"
              >
                Chat sekarang <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="rounded-3xl bg-white border border-[#E5E7EB] p-6">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B00] mb-4">
              Artikel lainnya
            </div>
            <ul className="space-y-4">
              {others.map((o) => (
                <li key={o.slug}>
                  <Link
                    to={`/blog/${o.slug}`}
                    data-testid={`related-${o.slug}`}
                    className="group block"
                  >
                    <div className="font-display font-semibold text-[#0F4C75] text-base leading-snug group-hover:text-[#FF6B00]">
                      {o.title}
                    </div>
                    <div className="text-xs text-[#1B262C]/55 mt-1">
                      {o.readingTime} · {o.category}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
